import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import Navbar from "./components/Navbar";
import { AuthProvider, useAuth } from "./context/AuthContext";
import HomePage from "./pages/HomePage";
import UnifiedPage from "./pages/UnifiedPage";
import MappingPage from "./pages/MappingPage";
import DatabaseUnifiedPage from "./pages/DatabaseUnifiedPage";
import DatabasePage from "./pages/DatabasePage";
import DatabaseMappingPage from "./pages/DatabaseMappingPage";
import DatabaseTablesPage from "./pages/DatabaseTablesPage"; // 📊 Alle Tabellen in Excel
import DetailsPage from "./pages/DetailsPage"; // 🔥 Neue Seite für Konto-Details
import StrgPage from "./pages/StrgPage";
import RenamePage from "./pages/RenamePage";
import DokumentUmbenennenPage from "./pages/rename/DokumentUmbenennenPage";
import RenameBatchPage from "./pages/RenameBatchPage";
import RenameEnginePage from "./pages/RenameEnginePage";
import MietverwaltungPage from "./pages/MietverwaltungPage";
import ZuordnenPage from "./pages/ZuordnenPage";
import VerschiebenPage from "./pages/VerschiebenPage";
import BilderVerschiebenPage from "./pages/BilderVerschiebenPage";
import FotosVerarbeitenPage from "./pages/FotosVerarbeitenPage";
import KomprimierungPage from "./pages/KomprimierungPage";
import ToolsKomprimierungPage from "./pages/ToolsKomprimierungPage";
import VbVergleichPage from "./pages/VbVergleichPage";
import ProtokollePage from "./pages/ProtokollePage";
import MonitoringPage from "./pages/MonitoringPage";
import RechnungenPage from "./pages/RechnungenPage";
import DatevVersandPage from "./pages/DatevVersandPage";
import PostkorbPage from "./pages/PostkorbPage";
import GeminiRegelnPage from "./pages/GeminiRegelnPage"; // Welle 75: Gemini-Regelwerk
import KrvPage from "./pages/KrvPage";
import WebRekorderPage from "./pages/WebRekorderPage";
import SkriptePage from "./pages/SkriptePage";
import ArbeitsnachweisPa from "./pages/ArbeitsnachweisPa";
import AdminPage from "./pages/AdminPage";
import ZeitApp from "./pages/ZeitApp";
import MonitoringWidget from "./components/MonitoringWidget";
import FahrtenbuchPage from "./pages/FahrtenbuchPage";
import ZaehlerstandPage from "./pages/ZaehlerstandPage";
import KiLernen from "./pages/KiLernen";
import GhostAssistent from "./components/chatbot/GhostAssistent";
import KiAdmin from "./pages/KiAdmin";
import ToolsPortalApp from "./portal/ToolsPortalApp";
import AdminToolsPage from "./pages/AdminToolsPage";
import VerwaltungPage from "./pages/VerwaltungPage";
import EinstellungenPage from "./pages/EinstellungenPage";
import { WELLE38_UI } from "./featureFlags";

// Externes Tools-Portal: eigene Domain, eigene Nutzer, eigener Login.
// Dev-Override: ?portal=1 (bleibt via localStorage bis zum Logout aktiv).
function isToolsPortalHost() {
  if (typeof window === "undefined") return false;
  if (window.location.hostname === "tools.sijelly.com") return true;
  if (new URLSearchParams(window.location.search).get("portal") === "1") {
    localStorage.setItem("portalMode", "1");
    return true;
  }
  return localStorage.getItem("portalMode") === "1";
}

function MappingRouteWrapper({ refreshTrigger }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialMode = params.get("mode") || localStorage.getItem("preferredViewMode") || "single";
  return <MappingPage key={refreshTrigger} refreshTrigger={refreshTrigger} initialMode={initialMode} />;
}

function AppInner() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { user, logout } = useAuth();
  const isAuthenticated = !!user;
  const location = useLocation();
  const isNurZeit = user?.zugang_typ === "nur_zeiterfassung";
  // User, deren Modul-Freigaben NUR vb_vergleich enthalten, bekommen
  // ausschließlich den PDF-Vergleich — ohne Navbar, Home oder andere Routen.
  const isNurVb =
    !!user &&
    Array.isArray(user.module) &&
    user.module.length > 0 &&
    user.module.every((m) => m === "vb_vergleich");

  // Viewport-Erkennung: ZeitApp-Maske nur auf Mobile (≤768 px). Auf Desktop
  // bekommen auch nur_zeiterfassung-User die volle ArbeitsnachweisPa-Maske.
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Externes Tools-Portal (tools.sijelly.com): komplett eigene App
  if (isToolsPortalHost()) {
    return <ToolsPortalApp />;
  }

  // /zeit bekommt kein IMHO-Layout (kein Navbar, kein GhostAssistent)
  if (location.pathname === "/zeit") {
    return <ZeitApp />;
  }

  // Nur-VB-User: immer und ausschließlich der PDF-Vergleich.
  // Statt „← Home" gibt es hier „Abmelden" — direkt, ohne Nachfrage.
  if (isNurVb) {
    return <VbVergleichPage onBack={logout} backLabel="⏻ Abmelden" />;
  }

  // nur_zeiterfassung-User auf Mobile → ZeitApp-Maske
  if (isNurZeit && isMobileViewport) {
    return <ZeitApp />;
  }

  // Auch Admin/Vollzugang bekommt auf Mobile bei /arbeitsnachweis die
  // mobile ZeitApp — der Desktop-Arbeitsnachweis ist auf dem Handy
  // unbedienbar. Andere Routen bleiben in der Volldienst-Sicht.
  if (isMobileViewport && location.pathname === "/arbeitsnachweis") {
    return <ZeitApp />;
  }

  // nur_zeiterfassung-User auf Desktop → Arbeitsnachweis-Seite
  if (isNurZeit && location.pathname !== "/arbeitsnachweis") {
    return <Navigate to="/arbeitsnachweis" replace />;
  }

  return (
    <>
      <Navbar isAuthenticated={isAuthenticated} />
      {!isNurZeit && (
        // Gemeinsame Ecke unten rechts: Ghost über dem Monitoring — nichts liegt übereinander
        <div className="ecke-unten-rechts">
          <GhostAssistent />
          <MonitoringWidget />
        </div>
      )}
      <Routes>
        <Route path="/" element={<HomePage />} /> 
        <Route path="/all-in-one" element={<UnifiedPage refreshTrigger={refreshTrigger} setRefreshTrigger={setRefreshTrigger} />} />
        <Route path="/mapping" element={<MappingRouteWrapper refreshTrigger={refreshTrigger} />} />
        <Route path="/mapping/single" element={<MappingPage key={refreshTrigger} refreshTrigger={refreshTrigger} initialMode="single" />} />
        <Route path="/mapping/batch" element={<MappingPage key={refreshTrigger} refreshTrigger={refreshTrigger} initialMode="batch" />} />
        <Route path="/database-import" element={<DatabaseUnifiedPage />} />
        <Route path="/data" element={<DatabasePage />} />
        <Route path="/database-tables" element={<DatabaseTablesPage />} />
        {/* Welle 97: dieselbe Seite auf der neuen Datenbank imho_v2 — mit Struktur-Werkzeugen */}
        <Route path="/database-tables-2" element={<DatabaseTablesPage key="v2" apiBase="/database/v2"
               titel="🧪 DB-Tabellen 2 — neue Datenbank (imho_v2)" mitSync={false} mitStruktur />} />
        <Route path="/database-mapping" element={<DatabaseMappingPage key={refreshTrigger} refreshTrigger={refreshTrigger} />} />
        <Route path="/details/:konto" element={<DetailsPage />} />
        <Route path="/strg" element={<StrgPage />} />
        <Route path="/rename" element={<DokumentUmbenennenPage />} />
        {/* Welle 100: alte Einzel-Ansicht (OCR-Overlay, Feld-Korrekturen) bleibt als Entwickler-Werkzeug */}
        <Route path="/rename-alt" element={<RenamePage />} />
        <Route path="/rename-batch" element={<RenameBatchPage />} />
        <Route path="/rename-engine" element={<RenameEnginePage />} />
        <Route path="/mietverwaltung" element={<MietverwaltungPage />} />
        <Route path="/zuordnen" element={<ZuordnenPage />} />
        <Route path="/zuordnen/verschieben" element={<VerschiebenPage />} />
        <Route path="/zuordnen/bilder" element={<BilderVerschiebenPage />} />
        <Route path="/zuordnen/fotos" element={<FotosVerarbeitenPage />} />
        <Route path="/zuordnen/komprimierung" element={<KomprimierungPage />} />
        <Route path="/zuordnen/bilder-komprimierung" element={<KomprimierungPage />} />
        <Route path="/fahrtenbuch" element={<FahrtenbuchPage />} />
        <Route path="/zaehlerstand" element={<ZaehlerstandPage />} />
        <Route path="/ki-lernen" element={<KiLernen />} />
        <Route path="/ki-admin" element={<KiAdmin />} />
        <Route path="/tools/komprimieren" element={<ToolsKomprimierungPage />} />
        <Route path="/tools/vb-vergleich" element={<VbVergleichPage />} />
        <Route path="/tools/protokolle" element={<ProtokollePage />} />
        <Route path="/monitoring" element={<MonitoringPage />} />
        <Route path="/rechnungen" element={<RechnungenPage />} />
        <Route path="/datev" element={<DatevVersandPage />} />
        <Route path="/postkorb" element={<PostkorbPage />} />
        <Route path="/zuordnen/ki-regeln" element={<GeminiRegelnPage />} />
        <Route path="/krv" element={<KrvPage />} />
        <Route path="/rekorder" element={<WebRekorderPage />} />
        <Route path="/skripte" element={<SkriptePage />} />
        <Route path="/arbeitsnachweis" element={<ArbeitsnachweisPa />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin-tools" element={<AdminToolsPage />} />
        {WELLE38_UI && <Route path="/verwaltung" element={<VerwaltungPage />} />}
        {WELLE38_UI && <Route path="/einstellungen" element={<EinstellungenPage />} />}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppInner />
      </Router>
    </AuthProvider>
  );
}

export default App;
